import {getCountryCode, getLanguage} from '@smart/locale-service';
import {Button, Markdown, Notification, Text} from '@smart/react-components';
import {isGlobalFeatureFlagsEnabled} from '@smart/utils';
import classnames from 'classnames';
import type {FormEventHandler} from 'react';
import {useEffect} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {transformMarkdown} from '../../../../utils/transform-markdown';
import type {ILiteRegisterRequest} from '../../../bff/post-form/mulesoft/types';
import {CarLineOptions} from '../../../bff/post-form/mulesoft/types';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {
  CountryCode,
  LangCode,
  ViewsFetchingToPostForm,
  availableCountries,
} from '../../common/types';
import {AppModelContext} from '../../components/app';
import {CountrySelect} from '../../components/country-select';
import Fields from '../../components/easy-form/fields';
import type {WrapperHandler} from '../../components/easy-form/types';
import {LegalLinks} from '../../components/legal-links';
import {MktLangSelect} from '../../components/mkt-lang-select';
import MultiCheckbox from '../../components/multi-checkbox';
import {useAPI} from '../../hooks/use-api';
import {useLocale} from '../../hooks/use-locale';
import {useTracking} from '../../hooks/use-tracking';
import type {ITrackNewsletter} from '../../hooks/use-tracking/types';
import {useViews} from '../../hooks/use-views';
import {useSchemas} from '../../hooks/user-schemas';
import newsletterStyles from './styles.module.scss';

const FEATURE_FLAG_ENABLE_RESERVE_MY_SEAT = false;

export interface INewsletterFormState {
  firstName: string;
  lastName: string;
  email: string;
  preferredLang: LangCode;
  enableVocAndMarketingConsent?: boolean;
  reserveSeat: boolean;
  lineItem?: ILiteRegisterRequest['lineItem'];
  country?: CountryCode;
  smart_vocConsentEmail_EU: boolean;
  postalCode?: string;
}

const viewName: ViewsFetchingToPostForm = `newsletter-form`;

export default function Newsletter(): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [formData, setFormData] = useState<INewsletterFormState>();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [mkt, setMkt] = useState<CountryCode>();
  const apiFetch = useAPI(viewName, true);
  const {setView} = useViews();
  const [trackStep] = useTracking<ITrackNewsletter>(
    `newsletter_rms`,
    `initial-nl`,
  );
  const {languageTag, marketId} = useLocale();
  const countryCode = getCountryCode(marketId);
  const language = getLanguage(languageTag);
  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppNewsletterUi || {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};
  const cmsTranslationsForLineItems = [
    cmsTranslations.newsletterForm_lineItem_launchEdition,
    cmsTranslations.newsletterForm_lineItem_premium,
    cmsTranslations.newsletterForm_lineItem_pro,
    cmsTranslations.newsletterForm_lineItem_brabus,
  ];

  const enableVocAndMarketingConsent = isGlobalFeatureFlagsEnabled(
    `ENABLE_MARKETING_VOC_CONSENT`,
    marketId,
  );

  // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
  // const cmsTranslationsErrors =
  //   React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
  //   {};
  // const cfmError = cmsTranslationsErrors.validations_serverError?.toString();
  const formWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackStep(1, `form_start`);
    trackStep(1, `Newsletter_started`);
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }
    if (!formData) return;

    setLoading(true);
    setServerError(undefined);

    const isRMS = FEATURE_FLAG_ENABLE_RESERVE_MY_SEAT && formData.reserveSeat;

    try {
      trackStep(1, `form_engagement`);
      await apiFetch({
        ...formData,
        enableVocAndMarketingConsent: !!enableVocAndMarketingConsent,
      });
      trackStep(1, `newsletter_submit`);
      isRMS ? setView(`contact-prefs`) : setView(`content-prefs`);
    } catch (error) {
      setServerError(error as string);
      setLoading(false);
      scrollToRef(formWrapperRef);
    }
  };

  const changeHandler: WrapperHandler = (state, errors) => {
    setHasErrors(!!errors);
    setFormData(state as unknown as INewsletterFormState);
    const selectedMKT = (state as any)?.country as CountryCode | undefined;
    if (!!selectedMKT) {
      setMkt(selectedMKT);
    } else if (!!mkt) {
      setMkt(undefined);
    }
  };

  const generalSchemas = useSchemas();

  const schemas = useMemo(() => {
    const {email, postalCode, firstAndLastNames} = generalSchemas;
    return {
      firstName: firstAndLastNames(true),
      lastName: firstAndLastNames(true),
      email: email(true),
      postalCode: postalCode(formData?.country, true),
    };
  }, [generalSchemas, formData?.country]);

  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  return (
    <div className={FormStyles.wrapper} ref={formWrapperRef}>
      <Fields.Wrapper
        translationPrefix={viewName}
        onUpdate={changeHandler}
        showErrors={submitAttempt}
        disabled={loading}
      >
        <Text className={FormStyles.upperText} variant="p-100" as="p">
          {cmsTranslations.newsletterForm_title}
        </Text>

        {serverError && (
          <Notification
            signal="error"
            className={FormStyles.serverError}
            onOpenChange={(open) => {
              if (!open) {
                setServerError(undefined);
              }
            }}
          >
            <Notification.Text>{setValueFromAEM()}</Notification.Text>
          </Notification>
        )}

        <form className={FormStyles.form} onSubmit={onSubmit}>
          <Fields.Group>
            <Fields.Text
              tag="firstName"
              required
              schema={schemas.firstName}
              label={cmsTranslations.newsletterForm_firstName}
            />
            <Fields.Text
              tag="lastName"
              required
              schema={schemas.lastName}
              label={cmsTranslations.newsletterForm_lastName}
            />
            <Fields.Text
              tag="email"
              required
              schema={schemas.email}
              label={cmsTranslations.newsletterForm_email}
            />

            <CountrySelect
              tag="country"
              required
              initial={countryCode}
              label={cmsTranslations.newsletterForm_country}
            />

            <div className={newsletterStyles.MktLangSelect}>
              <MktLangSelect
                initial={language}
                mkt={countryCode as (typeof availableCountries)[number]}
                tag="preferredLang"
                required
                label={cmsTranslations.newsletterForm_preferredLanguage}
              />
            </div>
          </Fields.Group>

          {FEATURE_FLAG_ENABLE_RESERVE_MY_SEAT && (
            <>
              <Fields.Checkbox
                tag="reserveSeat"
                props={{className: FormStyles.checkbox}}
                label={cmsTranslations.newsletterForm_reserveSeat}
              />
              {formData && formData[`reserveSeat`] && (
                <>
                  <MultiCheckbox
                    tag="lineItem"
                    options={CarLineOptions as unknown as string[]}
                    labels={cmsTranslationsForLineItems as string[]}
                  />
                  <Fields.Group>
                    <CountrySelect
                      required
                      tag="country"
                      label={cmsTranslations.newsletterForm_country}
                    />
                    <Fields.Text
                      required
                      tag="postalCode"
                      schema={schemas.postalCode}
                      label={cmsTranslations.newsletterForm_postalCode}
                    />
                  </Fields.Group>
                </>
              )}
            </>
          )}

          {enableVocAndMarketingConsent ? (
            <Markdown className={newsletterStyles.markdown}>
              {transformMarkdown(
                cmsTranslations?.marketingConsent?.markdown || ``,
              )}
            </Markdown>
          ) : (
            <LegalLinks
              className={FormStyles.legal}
              legalText={cmsTranslations.legal}
            />
          )}
          {enableVocAndMarketingConsent && (
            <div className={newsletterStyles.vocConsent}>
              <Fields.Checkbox
                tag="smart_vocConsentEmail_EU"
                key="smart_vocConsentEmail_EU"
                label=" "
              />
              <Markdown className={newsletterStyles.markdown}>
                {transformMarkdown(cmsTranslations?.vocConsent?.markdown || ``)}
              </Markdown>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className={classnames(FormStyles.button, {
              [newsletterStyles.subMitButton!]: enableVocAndMarketingConsent,
            })}
            disabled={(submitAttempt && hasErrors) || loading}
          >
            {cmsTranslations.newsletterForm_submit || `Submit`}
          </Button>
        </form>
      </Fields.Wrapper>
    </div>
  );
}
